import styled from "styled-components";
import { black, gray, grayDark, white } from "../styles/colors";
import { PhoneFrame } from "./PhoneFrame";
import { ScrollReveal } from "./ScrollReveal";

export type Feature = {
  id: string;
  eyebrow: string;
  title: string;
  description: string;
  bullets?: string[];
  posterSrc: string;
  videoSrc?: string;
  recordingLabel: string;
  alt: string;
  reversed?: boolean;
  dark?: boolean;
};

type FeatureSectionProps = {
  feature: Feature;
};

export function FeatureSection({ feature }: FeatureSectionProps) {
  const Section = feature.dark ? DarkSection : LightSection;

  return (
    <Section id={feature.id}>
      <Inner $reversed={feature.reversed}>
        <Copy $reversed={feature.reversed}>
          <ScrollReveal>
            <Eyebrow>{feature.eyebrow}</Eyebrow>
            <Title>{feature.title}</Title>
            <Description>{feature.description}</Description>
            {feature.bullets && (
              <BulletList>
                {feature.bullets.map((bullet) => (
                  <BulletItem key={bullet} $dark={feature.dark}>
                    <BulletDot $dark={feature.dark} aria-hidden="true" />
                    <span>{bullet}</span>
                  </BulletItem>
                ))}
              </BulletList>
            )}
          </ScrollReveal>
        </Copy>
        <Media $reversed={feature.reversed}>
          <ScrollReveal delay={1}>
            <PhoneFrame
              id={feature.id}
              posterSrc={feature.posterSrc}
              videoSrc={feature.videoSrc}
              alt={feature.alt}
              recordingLabel={feature.recordingLabel}
            />
          </ScrollReveal>
        </Media>
      </Inner>
    </Section>
  );
}

const sectionBase = {
  padding: "96px 24px",

  "@media (max-width: 768px)": {
    padding: "72px 20px",
  },
};

const LightSection = styled.section({
  ...sectionBase,
  background: white,
  color: black,
});

const DarkSection = styled.section({
  ...sectionBase,
  background: black,
  color: white,
});

const Inner = styled.div<{ $reversed?: boolean }>(({ $reversed }) => ({
  maxWidth: 1100,
  margin: "0 auto",
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 64,
  alignItems: "center",

  "@media (max-width: 900px)": {
    gridTemplateColumns: "1fr",
    gap: 40,
  },

  ...($reversed && {
    direction: "rtl",

    "& > *": {
      direction: "ltr",
    },
  }),
}));

const Copy = styled.div<{ $reversed?: boolean }>(({ $reversed }) => ({
  "@media (max-width: 900px)": {
    order: $reversed ? 2 : 1,
  },
}));

const Media = styled.div<{ $reversed?: boolean }>(({ $reversed }) => ({
  display: "flex",
  justifyContent: "center",
  width: "100%",

  "@media (max-width: 900px)": {
    order: $reversed ? 1 : 2,
  },
}));

const Eyebrow = styled.p({
  margin: "0 0 12px",
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: gray,
});

const Title = styled.h2({
  margin: "0 0 20px",
  fontSize: "clamp(32px, 4vw, 44px)",
  fontWeight: 700,
  lineHeight: 1.15,
  letterSpacing: "-0.03em",
});

const Description = styled.p({
  margin: 0,
  fontSize: 18,
  lineHeight: 1.75,
  color: grayDark,

  [`${DarkSection} &`]: {
    color: "rgba(253, 253, 253, 0.72)",
  },
});

const BulletList = styled.ul({
  margin: "24px 0 0",
  padding: 0,
  listStyle: "none",
  display: "grid",
  gap: 12,
});

const BulletItem = styled.li<{ $dark?: boolean }>(({ $dark }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: 12,
  fontSize: 16,
  lineHeight: 1.6,
  color: $dark ? "rgba(253, 253, 253, 0.72)" : grayDark,
}));

const BulletDot = styled.span<{ $dark?: boolean }>(({ $dark }) => ({
  flexShrink: 0,
  width: 8,
  height: 8,
  marginTop: 9,
  borderRadius: "50%",
  background: $dark ? white : black,
}));
